/**
 * @author NikolaV
 */
import { Ajax2 } from '@shared/Ajax';
import * as React from 'react';


export interface IProjectCode {
    code: string;
    type: string;
    description?: string;
    shortDescription?: string;
}

let projectCodes: Record<string, IProjectCode> = {};
let projectCodesJustCodes: string[] = [];
let projectCodesJustCodesByType: Record<string, string[]> = {};
let isLoaded = false;
let loadPromise: Promise<IProjectCode[]> = null;

function fetchCodes() {
    if (isLoaded) {
        return Promise.resolve(projectCodes);
    }
    if (loadPromise) {
        return loadPromise;
    }

    loadPromise = Ajax2({
        url: kuura.getContextPath() + '/api/1/project/' + kuura.getProjectId() + '/codes.json',
    }).then(data => data.codes);

    loadPromise.then((codes: IProjectCode[]) => {
        isLoaded = true;
        projectCodesJustCodes = Array.from(new Set(codes.map(c => c.code)));

        projectCodes = {};
        projectCodesJustCodesByType = {};
        codes.forEach((code: IProjectCode) => {
            projectCodes[code.code] = code;

            if (!projectCodesJustCodesByType[code.type]) {
                projectCodesJustCodesByType[code.type] = [];
            }
            projectCodesJustCodesByType[code.type].push(code.code);
        });
    }).finally(() => {
        loadPromise = null;
    });

    return loadPromise;
}

function get(code: string): IProjectCode {
    return projectCodes[code];
}

function getDescription(code: string): string {
    const codeDef = projectCodes[code];
    return codeDef?.description || codeDef?.shortDescription || null;
}

function codeWithDescription(code): string {
    if (!code) return '';

    const fullCode = get(code);
    if (!fullCode) return code;

    return `${code} (${fullCode.type}) (${fullCode.shortDescription || fullCode.description})`;
}

const ProjectCodeSpan = ({ code }) => <span title={codeTooltip(code)}>{codeWithDescription(code)}</span>;

function codeTooltip(code): string {
    if (!code) return '';

    const fullCode = get(code);
    if (!fullCode) return '';

    return `${kuura.getMessage('tablet.code_js')}: ${code}
${kuura.getMessage('general.type')}: ${fullCode.type}
${kuura.getMessage('general.description')}: ${fullCode.description || fullCode.shortDescription}`;
}

export const ProjectCodesService = {
    fetchCodes,
    get,
    getDescription,
    codeWithDescription,
    getCodesArray: () => projectCodesJustCodes,
    getCodesArrayByType: (type: string) => projectCodesJustCodesByType[type] || [],
    codeTooltip,
    ProjectCodeSpan
};
